import "./style.scss";

import { formatSocialProfileType, formatVatType } from "components/Beneficiar/utils";
import ModalBox from "components/Modal";
import { downloadFile, formatLocaleDateString, formatNumber } from "helpers/utils";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { DAS2DeclarationLine } from "store/contributionStore";
import { downloadDeclaration, fetchContribution } from "store/contributionStore";
import { useAppDispatch, useAppSelector } from "store/store";

import LastElementTable from "../LastElementTable";
import type { LastElementTableColumns } from "../LastElementTable/type";
import { Spinner } from "../Spinner";
import StyledButton from "../StyledButton";

interface PreviewDeclarationProps {
    showModal: boolean;
    onClose: () => void;
    contributionId?: number;
}

const PreviewDeclaration = (props: PreviewDeclarationProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { showModal, onClose, contributionId } = props;
    const { contribution, contributionStatus } = useAppSelector((state) => state.contribution);

    const totalGrossDuty = useMemo(
        () => contribution?.lines?.reduce((acc, line) => acc + line.grossDuty / 100, 0) ?? 0,
        [contribution],
    );

    useEffect(() => {
        if (showModal) dispatch(fetchContribution({ id: contributionId ?? -1 }));
    }, [showModal]);

    const handleReDownload = async () => {
        if (contributionId) {
            const response = await dispatch(downloadDeclaration({ id: contributionId }));
            if (response.meta.requestStatus === "fulfilled") {
                if (response.payload && contribution) {
                    const fileName =
                        contribution?.type === "das2"
                            ? t("Declarations.FileDownload.DAS2", { year: contribution?.year })
                            : contribution?.quarter > 0
                              ? t("Declarations.FileDownload.URSSAFQuarter", {
                                    year: contribution?.year,
                                    quarter: contribution?.quarter,
                                })
                              : t("Declarations.FileDownload.URSSAFYear", { year: contribution?.year });
                    downloadFile({ source: response.payload, fileName });
                }
            }
        }
    };

    const format = (value: any) => value || "-";

    const contributionColumns: LastElementTableColumns[] = [
        {
            id: "lastname",
            label: t("Declare.PreviewLastname"),
            align: "left",
            sticky: "left",
            format,
        },
        {
            id: "firstname",
            label: t("Declare.PreviewFirstname"),
            align: "left",
            format,
        },
        {
            id: "socialProfileType",
            label: t("Declare.PreviewSocialProfileType"),
            minWidth: "50px",
            format,
        },
        {
            id: "socialSecurityNumber",
            label: t("Declare.PreviewSocialSecurityNumber"),
            align: "left",
            format,
        },
        { id: "street", label: t("Declare.PreviewStreet"), align: "left", format },
        {
            id: "zipCode",
            label: t("Declare.PreviewZipCode"),
            align: "left",
            format,
        },
        { id: "city", label: t("Declare.PreviewCity"), align: "left", format },
        {
            id: "country",
            label: t("Declare.PreviewCountry"),
            align: "left",
            format,
        },
        {
            id: "birthDate",
            label: t("Declare.PreviewBirthDate"),
            align: "left",
            format: (value) => formatLocaleDateString({ date: value }),
        },
        {
            id: "incomeNature",
            label: t("Declare.PreviewIncomeNature"),
            align: "left",
            format,
        },
        {
            id: "artworkNature",
            label: t("Declare.PreviewArtworkNature"),
            align: "left",
            format,
        },
        {
            id: "paymentDate",
            label: t("Declare.PreviewPaymentDate"),
            align: "left",
            format: (value) => formatLocaleDateString({ date: value }),
        },
        {
            id: "siretNumber",
            label: t("Declare.PreviewSiretNumber"),
            align: "left",
            format,
        },
        {
            id: "grossDuty",
            label: t("Declare.PreviewGrossDuty"),
            align: "left",
            sticky: "right",
            format: (value) => formatNumber(value / 100, { isCurrency: true, always2Digit: true }),
        },
    ];
    const das2Columns: LastElementTableColumns[] = [
        {
            id: "lastname",
            label: t("Declare.PreviewLastname"),
            align: "left",
            sticky: "left",
            format,
        },
        {
            id: "firstname",
            label: t("Declare.PreviewFirstname"),
            align: "left",
            format,
        },
        { id: "street", label: t("Declare.PreviewStreet"), align: "left", format },
        {
            id: "zipCode",
            label: t("Declare.PreviewZipCode"),
            align: "left",
            format,
        },
        { id: "city", label: t("Declare.PreviewCity"), align: "left", format },
        {
            id: "country",
            label: t("Declare.PreviewCountry"),
            align: "left",
            format,
        },
        {
            id: "birthDate",
            label: t("Declare.PreviewBirthDate"),
            align: "left",
            format: (value) => formatLocaleDateString({ date: value }),
        },
        {
            id: "activityType",
            label: t("Declare.PreviewActivity"),
            minWidth: "50px",
            format,
        },
        {
            id: "precount",
            label: t("Declare.PreviewPrecount"),
            align: "left",
            format: (value, id, currentRow: DAS2DeclarationLine) =>
                formatSocialProfileType({ item: currentRow, isCompanyBelgian: true }) || "-",
        },
        {
            id: "vat",
            label: t("Declare.PreviewVAT"),
            align: "left",
            format: (value: number) => formatVatType(value) || "-",
        },
        {
            id: "siretNumber",
            label: "SIRET",
            align: "left",
            format,
        },
        {
            id: "paymentDate",
            label: t("Declare.PreviewPaymentDate"),
            align: "left",
            format: (value) => formatLocaleDateString({ date: value }),
        },
        {
            id: "grossDuty",
            label: t("Declare.PreviewGrossDuty_2"),
            align: "right",
            format: (value) => formatNumber(value / 100, { isCurrency: true, always2Digit: true }),
        },
        {
            id: "rightsVat",
            label: t("Declare.PreviewNetVat"),
            align: "right",
            format: (value) => formatNumber(value / 100, { isCurrency: true, always2Digit: true }),
        },
        {
            id: "withheldPercent",
            label: t("Declare.PreviewWithheldVat"),
            align: "right",
            format: (value) => `${value / 100}%`,
        },
        {
            id: "withheldForeign",
            label: t("Declare.PreviewWithheldForeign"),
            align: "right",
            format: (value) => `${value / 100}%`,
        },
        {
            id: "rightsWithVat",
            label: t("Declare.PreviewNetPaid"),
            align: "right",
            sticky: "right",
            format: (value) => formatNumber(value / 100, { isCurrency: true, always2Digit: true }),
        },
    ];

    return contribution ? (
        <ModalBox
            open={showModal}
            onClose={() => onClose()}
            mainTitle={t("Declare.PreviewURSSAFTitle", {
                count: contribution.quarter,
                year: contribution.year,
            })}
            subTitle={t("Declare.PreviewURSSAFSubTitle", {
                count: contribution.lines?.length ?? 0,
                totalGrossDuty: formatNumber(totalGrossDuty, {
                    isCurrency: true,
                    always2Digit: true,
                }),
            })}
        >
            {contributionStatus == "pending" ? (
                <div style={{ textAlign: "center" }}>
                    <Spinner />
                </div>
            ) : (
                <div className="preview-declaration">
                    {contribution.lines && contribution?.lines.length > 0 ? (
                        <LastElementTable
                            columns={contribution.type === "urssaf" ? contributionColumns : das2Columns}
                            data={contribution.lines ?? []}
                            max={9999}
                            bottomFooterContent={<></>}
                            maxHeight={450}
                        />
                    ) : (
                        <span className="no-data-info">{t("Declarations.PreviewModal.NoData")}</span>
                    )}
                    <StyledButton buttonType="primary" onClick={handleReDownload}>
                        {t("Declare.PreviewReDownload")}
                    </StyledButton>
                </div>
            )}
        </ModalBox>
    ) : (
        <></>
    );
};

export default PreviewDeclaration;
